import React, { useState, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import productData from "../Datas/productData";
import Footer from "./Footer";
import "../css/productdetails.scss";
import Header from "./Header";
import { CartContext } from "./CartContext";
import { FaStar, FaRegStar, FaMinus, FaPlus } from "react-icons/fa";
import { BsHandbag } from "react-icons/bs";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === parseInt(id));
  const { addToCart } = useContext(CartContext);

  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || ""
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (!product) {
    return (
      <div className="product-detail">
        <Header />
        <div className="product-detail__not-found">
          <h2>Product not found</h2>
          <Link onClick={toTop} to="/shop">
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    navigate("/cartpage");
    toTop();
  };

  const incrementQuantity = () => setQuantity((q) => q + 1);
  const decrementQuantity = () => setQuantity((q) => Math.max(1, q - 1));

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<FaStar key={i} className="star filled" />);
      } else {
        stars.push(<FaRegStar key={i} className="star empty" />);
      }
    }
    return stars;
  };

  const getColorCode = (colorName) => {
    const colorMap = {
      "Ocean Blue": "#1E90FF",
      "Coral Pink": "#FF6B6B",
      "Seafoam Green": "#90EE90",
      "Deep Blue": "#00008B",
      "Sky Blue": "#87CEEB",
      "Classic Red": "#DC143C",
      "Coral": "#FF7F50",
      "Grey": "#808080",
      "White": "#FFFFFF",
      "Navy": "#000080",
      "Teal": "#008080",
      "Sand": "#F4A460",
      "Purple": "#800080",
      "Blue": "#0000FF",
      "Pink": "#FFC0CB",
      "Yellow": "#FFD700",
      "Black": "#000000",
      "Red": "#FF0000",
      "Green": "#228B22",
      "Orange": "#FFA500",
      "Brown": "#8B4513",
      "Dark Grey": "#4A4A4A",
      "Bioluminescent Blue": "#00FFFF",
      "Spotted": "#FFFFFF",
      "Golden Brown": "#DAA520",
      "Cinnamon": "#D2691E",
      "Cream": "#FFFDD0",
      "Forest Green": "#228B22",
      "Red Berries": "#C41E3A",
      "Red Spotted": "#FF0000",
      "Rainbow": "linear-gradient(90deg, #FF0000, #FF7F00, #FFFF00, #00FF00, #0000FF)",
      "Pastel": "linear-gradient(90deg, #FFB6C1, #E6E6FA, #B0E0E6)",
      "Confetti Colors": "linear-gradient(90deg, #FF6B6B, #4ECDC4, #FFE66D)",
      "Birthday Cake Design": "#FF69B4",
      "Gift Box": "#9B59B6",
      "Party Hat": "#3498DB",
      "Red Cap": "#DC143C",
      "Orange Cap": "#FFA500",
      "Purple Cap": "#800080",
      "Multi-colored Ocean Palette": "linear-gradient(90deg, #1E90FF, #90EE90, #FF6B6B)",
      "Bold Colors": "linear-gradient(90deg, #FF0000, #0000FF, #FFFF00)",
    };

    if (colorMap[colorName]) return colorMap[colorName];
    return "#CCCCCC";
  };

  return (
    <div className="product-detail">
      <Header />

      <div className="product-detail__breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/shop">Shop</Link>
        <span>/</span>
        <span className="current">{product.name}</span>
      </div>

      <div className="product-detail__content">
        <div className="product-gallery">
          <div className="gallery-main">
            <img
              src={product.images[selectedImage] || product.image}
              alt={product.name}
              className="main-image"
            />
          </div>
          {product.images.length > 1 && (
            <div className="gallery-thumbnails">
              {product.images.map((img, index) => (
                <div
                  key={index}
                  className={`thumbnail ${selectedImage === index ? "active" : ""}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={img} alt={`${product.name} view ${index + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="product-info">
          <span className="product-brand">{product.category}</span>
          <h1 className="product-title">{product.name}</h1>

          <div className="product-rating">
            <div className="stars">{renderStars(parseFloat(product.rating))}</div>
            <span className="rating-number">{product.rating}</span>
            <span className="review-count">({product.reviews} reviews)</span>
          </div>

          <p className="product-price">${product.price}</p>

          <div className="product-options">
            <div className="option-group">
              <label className="option-label">Color: <span className="selected-value">{selectedColor}</span></label>
              <div className="color-options">
                {product.colors.map((color, index) => (
                  <button
                    key={index}
                    className={`color-swatch ${selectedColor === color ? "selected" : ""}`}
                    style={{ background: getColorCode(color) }}
                    onClick={() => setSelectedColor(color)}
                    title={color}
                  />
                ))}
              </div>
            </div>

            {product.size[0] !== "One Size" && product.size[0] !== "One Size Set" && (
              <div className="option-group">
                <label className="option-label">Size: <span className="selected-value">{selectedSize}</span></label>
                <div className="size-options">
                  {product.size.map((size, index) => (
                    <button
                      key={index}
                      className={`size-btn ${selectedSize === size ? "selected" : ""}`}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="option-group">
              <label className="option-label">Quantity</label>
              <div className="quantity-selector">
                <button className="qty-btn" onClick={decrementQuantity}>
                  <FaMinus />
                </button>
                <span className="qty-value">{quantity}</span>
                <button className="qty-btn" onClick={incrementQuantity}>
                  <FaPlus />
                </button>
              </div>
            </div>
          </div>

          <div className="product-actions">
            <button className="btn-add-to-cart" onClick={handleAddToCart}>
              <BsHandbag className="btn-icon" />
              Add to Bag
            </button>

          </div>

          <div className="product-meta">
            <p className="material-info">
              <strong>Material:</strong> {product.material}
            </p>
          </div>

          <div className="product-tabs">
            <div className="tab-buttons">
              <button
                className={`tab-btn ${activeTab === "description" ? "active" : ""}`}
                onClick={() => setActiveTab("description")}
              >
                Description
              </button>
              <button
                className={`tab-btn ${activeTab === "features" ? "active" : ""}`}
                onClick={() => setActiveTab("features")}
              >
                Features
              </button>
            </div>
            <div className="tab-content">
              {activeTab === "description" && (
                <ul className="description-list">
                  {product.description.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              )}
              {activeTab === "features" && (
                <ul className="features-list">
                  {product.Features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
